import React, { useCallback, useState } from "react";
import {
  useForwardAction,
  DYNAMIC_FORWARD_BACKUP_NONE,
} from "../../hooks/useCallAction";
import ForwardUI from "./ForwardUI";
import { useRouteConfigModals } from "../contexts/RouteConfigModalsContext";
import { resolveForwardNumberPhone } from "../../utils/forwardNumberChannels";

/**
 * Forward component — unified Simple / Dynamic Forward card.
 * @param {string} context - 'root' | 'offHours' (defaults to 'root')
 */
const Forward = ({
  setOpenConfigureSlotModal,
  route,
  setRoute,
  context = "root",
  hasValidationError = false,
}) => {
  const routeModals = useRouteConfigModals();
  const [localRecordings] = useState(
    () => JSON.parse(localStorage.getItem("organization"))?.recordings?.L || []
  );
  const [localChannels] = useState(
    () => JSON.parse(localStorage.getItem("organization"))?.channels?.L || []
  );
  const recordings = routeModals?.recordings ?? localRecordings;
  const channels = routeModals?.channels ?? localChannels;
  const forwardLogic = useForwardAction(context, route, setRoute);

  const applyResolvedPhone = useCallback(
    (phone, index, currentList) => {
      if (phone === DYNAMIC_FORWARD_BACKUP_NONE) {
        forwardLogic.handleNumberChange(DYNAMIC_FORWARD_BACKUP_NONE);
        return;
      }
      if (typeof index === "number" && Array.isArray(currentList)) {
        const next = [...currentList];
        if (index < next.length) {
          next[index] = phone;
        } else {
          next.push(phone);
        }
        forwardLogic.handleDestinationsChange(next);
        return;
      }
      forwardLogic.handleNumberChange(phone);
    },
    [forwardLogic.handleNumberChange, forwardLogic.handleDestinationsChange]
  );

  const handleDestinationChange = useCallback(
    (value, index, currentList) => {
      if (value === DYNAMIC_FORWARD_BACKUP_NONE) {
        applyResolvedPhone(DYNAMIC_FORWARD_BACKUP_NONE, index, currentList);
        return;
      }
      const phone = resolveForwardNumberPhone(value, channels);
      applyResolvedPhone(phone, index, currentList);
    },
    [channels, applyResolvedPhone]
  );

  const getAddDestinationOption = useCallback(
    (index) => {
      if (!routeModals) return undefined;
      return {
        label: "Add Forwarding Number",
        onClick: () =>
          routeModals.openAddChannelModal("forward_number", (value) => {
            const list = forwardLogic.destinationValues || [];
            handleDestinationChange(value, index, list);
          }),
      };
    },
    [routeModals, forwardLogic.destinationValues, handleDestinationChange]
  );

  const addDestinationOption = getAddDestinationOption(0);
  const addRecordingOption = routeModals
    ? {
        label: "Add Recording",
        onClick: () =>
          routeModals.openAddRecordingModal(forwardLogic.handleRecordingChange),
      }
    : undefined;

  return (
    <ForwardUI
      {...forwardLogic}
      setOpenConfigureSlotModal={setOpenConfigureSlotModal}
      channels={channels}
      recordings={recordings}
      hasValidationError={hasValidationError}
      addDestinationOption={addDestinationOption}
      getAddDestinationOption={getAddDestinationOption}
      addRecordingOption={addRecordingOption}
      onDestinationChange={handleDestinationChange}
    />
  );
};

export default Forward;
